import { BaseProvider } from "./base.provider";
import { BaseRequestStaticServer } from "../servers/base-request.server";
import { ResultType } from "../../models/common";
import { ResourcesType, ResourcesSpecified, ResourceLabelStatus, ForecastDay, WeatherTimeReturn,
  WeatherHistorySumTmpApcp } from "../../models/big-screen";
import { ITable } from "@gago/frame";

interface CodeParams {
  /** 区域代码 */
  code?: string;
  /** 作物类型 */ // 资源类型 水稻: rice 耕地: arable
  type?: string;
}
/**
 * 大屏页面数据服务层
 * @author 马俊峰
 * @date 2020-07-28
 * @export
 * @class BigScreenProvider
 * @extends {BaseProvider}
 */
export default class BigScreenProvider extends BaseProvider {
  /** 资源概况 */
  static async getResourceType(params: CodeParams): Promise<ITable<1, 1>> {
    const { data } = await BaseRequestStaticServer.get<ResultType<ResourcesType[]>>("/resource", { params });
    let total = 0;
    const tableData: ITable<1, 1>["data"] = [];
    for (const iterator of data) {
      tableData.push(["", iterator.typeName, iterator.area]);
      total += iterator.area;
    }
    total = Number(total.toFixed(2));
    return {
      title: `${total}`,
      subtitle: "总面积(万亩)",
      head: ["ID", "类型", "面积"],
      range: [[], [], []],
      prefix: ["", "", ""],
      suffix: ["", "", "万亩"],
      defaultValue: [undefined, undefined, undefined],
      data: tableData,
    };
  }
  /** 指定获取基础资源五年数据 */
  static async getResourceHistory(params: CodeParams): Promise<ITable<2, 1>> {
    const { data } = await BaseRequestStaticServer.get<ResultType<ResourcesSpecified[]>>("/resource/history", { params });
    const tableData = data.map<ITable<2, 1>["data"][0]>(item => ["", item.typeName, `${item.year}`, item.area]);
    return {
      title: "",
      subtitle: "",
      head: ["ID", "类型", "年份", "单位"],
      range: [[], [], [], []],
      prefix: ["", "", "", ""],
      suffix: ["", "", "", "万亩"],
      defaultValue: [undefined, undefined, undefined, undefined],
      data: tableData,
    };
  }
  /** 指定资源地图上方标签数据 */
  static async getResourceLabel(params: CodeParams) {
    const { data } = await BaseRequestStaticServer.get<ResultType<ResourceLabelStatus>>("/resource/label", { params });
    return data;
  }
  /** 农业气象服务 */
  static async getForecastShort(params: CodeParams) {
    const { data } = await BaseRequestStaticServer.get<ResultType<ForecastDay[]>>("/weather/forecast/short", { params });
    return data;
  }
  /** 气候趋势分析，积温 */
  static async getClimateTrendAt(params: CodeParams): Promise<ITable<1, 5>> {
    const { data } = await BaseRequestStaticServer.get<ResultType<WeatherTimeReturn>>("/weather/climate/trend",
    { params: { ...params, type: 1 } });
    const jyouxiao = Number((data.current / 8000).toFixed(2));
    const jshinian = Number((data.history / 8000).toFixed(2));
    const jzengzhang = Number(((data.current - data.history) / data.history * 100).toFixed(2));
    return {
      title: ``,
      subtitle: "",
      description: "",
      defaultValue: [0, 0, 0, 0, 0, 0, 0],
      range: [[], [], [], [], [], [], []],
      head: ["ID", "类型", "标记", "活动积温", "五年平均", "当前值", "增长率"],
      data: [["0", "活动积温", jshinian, jyouxiao, data.history, data.current, jzengzhang]],
      suffix: ["", "", "℃·d", "℃·d", "℃·d", "℃·d", "%"],
    };
  }
  /** 气候趋势分析，降水 */
  static async getClimateTrendApcp(params: CodeParams): Promise<ITable<1, 5>> {
    const { data } = await BaseRequestStaticServer.get<ResultType<WeatherTimeReturn>>("/weather/climate/trend",
    { params: { ...params, type: 2 } });
    const youxiao = Number((data.current / 5000).toFixed(2));
    const shinian = Number((data.history / 5000).toFixed(2));
    const zengzhang = Number(((data.current - data.history) / data.history * 100).toFixed(2));
    return {
      title: ``,
      subtitle: "",
      description: "",
      defaultValue: [0, 0, 0, 0, 0, 0, 0],
      range: [[], [], [], [], [], [], []],
      head: ["ID", "类型", "标记", "累积降水", "五年平均", "当前值", "增长率"],
      data: [["0", "累积降水", shinian, youxiao, data.history, data.current, zengzhang]],
      suffix: ["", "", "mm", "mm", "mm", "mm", "%"],
    };
  }
  /** 历史气象柱状图 */
  static async getWeatherHistory(params: CodeParams): Promise<ITable<1, 2>> {
    const { data } = await BaseRequestStaticServer.get<ResultType<WeatherHistorySumTmpApcp[]>>("/weather/history", { params });
    const tableData: ITable<1, 2>["data"] = [];
    for (const iterator of data) {
      tableData.push(["", `${iterator.year}`, iterator.apcp, iterator.tmp]);
    }
    return {
      title: "",
      subtitle: "",
      head: ["ID", "类型", "降水", "积温"],
      range: [[], [], [], []],
      prefix: ["", "", "", ""],
      suffix: ["", "", "mm", "℃·d"],
      defaultValue: [undefined, undefined, undefined, undefined],
      data: tableData,
    };
  }
}
/** 未来七天气象预报数据格式转换 */
export function forecastShortItable(data: ForecastDay[]): ITable<5, 4> {
  let tableData: ITable<5, 4>["data"] = [];
  if (data.length) {
    // tslint:disable-next-line:ter-max-len
    tableData = data.map(item => ["", item.weeks, item.time, item.weatherDescription, item.windDirection, `${item.windSpeed}级`, item.tmax, item.tmin, item.apcp, item.humidity]);
  } else {
    tableData = [
      ["", "星期二", "05/26", "多云", "北风转南风", "3-4级", 26, 16, 0, 67.06],
      ["", "星期三", "05/27", "多云", "北风转南风", "3-4级", 29, 16, 0, 53.04],
      ["", "星期四", "05/28", "多云", "南风", "3-4级", 33, 20, 0, 45.28],
      ["", "星期五", "05/29", "多云转晴", "南风", "3-4级", 34, 19, 0, 51.96],
      ["", "星期六", "05/30", "多云转小雨", "南风", "3-4级", 31, 18, 0.01, 55.82],
      ["", "星期日", "05/31", "小雨转晴", "北风转南风", "3-4级转<3级", 29, 19, 0.49, 62.8],
      ["", "星期一", "06/01", "小雨", "南风转东南风", "<3级转3-4级", 31, 21, 1.1, 52.38],
    ];
  }
  return {
    title: "天气预报",
    subtitle: "",
    description: "",
    defaultValue: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    range: [[], [], [], [], [], [], [], [], [], []],
    head: ["ID", "周几", "日期", "天气", "风向", "风级", "最高温", "最低温", "降雨", "湿度"],
    data: tableData,
    suffix: ["", "", "", "", "", "级", "°", "°", "mm", "%"],
  };
}
